import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../services/authService";
import PasswordInput from "../PasswordInput";
import {
  validateRequiredField,
  validateEmailField,
} from "../../utils/validation";
import {
  canManageClients,
  canManageFirmware,
  canDownloadFirmware,
  canDownloadClientDocs,
  normalizeRole,
  normalizeStatus,
} from "../../services/authz";
import "./LoginForm.scss";

function resolvePostLoginRoute(statusInput, roleInput) {
  const status = normalizeStatus(statusInput);
  const role = normalizeRole(roleInput);

  if (canManageClients(status, role)) {
    return "/admin";
  }

  if (canManageFirmware(status, role)) {
    return "/service-admin/firmware";
  }

  if (canDownloadFirmware(status, role)) {
    return "/firmware";
  }

  if (canDownloadClientDocs(status, role)) {
    return "/dashboard";
  }

  return "/dashboard";
}

export default function LoginForm() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [resetFieldErrors, setResetFieldErrors] = useState({});

  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Update login form values and clear field-level errors.
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Update reset email and clear field-level errors.
  const handleResetEmailChange = (e) => {
    setResetEmail(e.target.value);
    setResetFieldErrors((prev) => ({ ...prev, resetEmail: "" }));
  };

  // Validate login form using shared helpers.
  const validateLoginForm = () => {
    const nextErrors = {};
    const requiredMessage = t("validation.required");
    const emailMessage = t("validation.email");

    validateEmailField(
      nextErrors,
      "email",
      form.email,
      requiredMessage,
      emailMessage,
    );
    validateRequiredField(
      nextErrors,
      "password",
      form.password,
      requiredMessage,
    );

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Validate password reset form using shared helpers.
  const validateResetForm = () => {
    const nextErrors = {};

    validateEmailField(
      nextErrors,
      "resetEmail",
      resetEmail,
      t("validation.required"),
      t("validation.email"),
    );

    setResetFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Handle Firebase login and navigate directly to the correct destination.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResetMessage("");
    setResetError("");

    if (!validateLoginForm()) return;

    try {
      const result = await login(form.email, form.password);
      const targetRoute = resolvePostLoginRoute(
        result?.userStatus,
        result?.userRole,
      );

      navigate(targetRoute, { replace: true });
    } catch (err) {
      setError(t(["auth.errors.invalidLogin", "error_login_invalid"]));
    }
  };

  // Handle password reset flow.
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetMessage("");
    setResetError("");

    if (!validateResetForm()) return;

    try {
      await resetPassword(resetEmail);
      setResetMessage(
        t(["auth.messages.resetEmailSent", "msg_reset_email_sent"]),
      );
    } catch (err) {
      console.error("Password reset failed:", err);
      setResetError(t(["auth.errors.resetEmail", "error_reset_email"]));
    }
  };

  const toggleResetMode = () => {
    setResetMode((prev) => !prev);
    setError("");
    setResetMessage("");
    setResetError("");
    setFieldErrors({});
    setResetFieldErrors({});
    setResetEmail(form.email);
  };

  return (
    <form
      onSubmit={resetMode ? handlePasswordReset : handleSubmit}
      className="login-form auth-form auth-form--login"
      noValidate
    >
      <h1 className="login-form__title">
        {resetMode
          ? t(["auth.resetTitle", "title_password_reset"])
          : t(["auth.loginTitle", "title_login"])}
      </h1>

      {resetMode ? (
        <>
          <div className="form-group">
            <label htmlFor="resetEmail">{t(["auth.email", "email"])}</label>
            <input
              id="resetEmail"
              name="resetEmail"
              type="email"
              placeholder={t(["auth.emailPlaceholder", "email_placeholder"])}
              value={resetEmail}
              onChange={handleResetEmailChange}
              aria-invalid={Boolean(resetFieldErrors.resetEmail)}
            />
            {resetFieldErrors.resetEmail && (
              <p className="error-text">{resetFieldErrors.resetEmail}</p>
            )}
          </div>

          <button type="submit">
            {t(["auth.sendResetEmailButton", "btn_send_reset_email"])}
          </button>

          {resetMessage && <p className="success-text">{resetMessage}</p>}
          {resetError && <p className="error-text">{resetError}</p>}
        </>
      ) : (
        <>
          {error && <p className="error-text">{error}</p>}

          <div className="form-group">
            <label htmlFor="email">{t(["auth.email", "email"])}</label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              aria-invalid={Boolean(fieldErrors.email)}
            />
            {fieldErrors.email && (
              <p className="error-text">{fieldErrors.email}</p>
            )}
          </div>

          <PasswordInput
            id="password"
            name="password"
            label={t(["auth.password", "password"])}
            value={form.password}
            onChange={handleChange}
            ariaInvalid={Boolean(fieldErrors.password)}
            required={false}
          />
          {fieldErrors.password && (
            <p className="error-text">{fieldErrors.password}</p>
          )}

          <button type="submit">{t(["auth.loginButton", "btn_login"])}</button>
        </>
      )}

      <div className="forgot-password">
        <button type="button" onClick={toggleResetMode} className="link-button">
          {resetMode
            ? t(["auth.backToLoginButton", "btn_back_to_login"])
            : t(["auth.forgotPasswordLink", "link_forgot_password"])}
        </button>
      </div>
    </form>
  );
}
